if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        const currentVariantMedia = this.querySelector(
          ".is-current-variant-media",
        );

        this.lastVariantMediaId = currentVariantMedia
          ? parseInt(currentVariantMedia.dataset.mediaId, 10)
          : null;
        this.stackSeeMoreLimit = parseInt(
          this.dataset.stackSeeMoreLimit || "7",
          10,
        );
        this.boundOnSlideChanged = this.onSlideChanged.bind(this);
        this.boundOnThumbnailClick = this.onThumbnailClick.bind(this);
        this.boundSyncStackSeeMore = this.syncStackSeeMore.bind(this);
        this.mobileMediaQuery = window.matchMedia("(max-width: 749px)");

        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((thumbnail) => {
              thumbnail.addEventListener("click", this.boundOnThumbnailClick);
            });
        }

        this.addEventListener("sliderChanged", this.boundOnSlideChanged);

        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener(
            "change",
            this.boundSyncStackSeeMore,
          );
        } else if (this.mobileMediaQuery.addListener) {
          this.mobileMediaQuery.addListener(this.boundSyncStackSeeMore);
        }

        this.syncStackSeeMore();
      }

      disconnectedCallback() {
        this.removeEventListener("sliderChanged", this.boundOnSlideChanged);

        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((thumbnail) => {
              thumbnail.removeEventListener(
                "click",
                this.boundOnThumbnailClick,
              );
            });
        }

        if (this.mobileMediaQuery?.removeEventListener) {
          this.mobileMediaQuery.removeEventListener(
            "change",
            this.boundSyncStackSeeMore,
          );
        } else if (this.mobileMediaQuery?.removeListener) {
          this.mobileMediaQuery.removeListener(this.boundSyncStackSeeMore);
        }
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      get ifMediaStack() {
        return this.hasAttribute("data-media-stack");
      }

      get stackSeeMore() {
        return this.querySelector(".product-media-stack-see-more");
      }

      get stackSeeMoreOpener() {
        return this.querySelector(".product-media-stack-see-more-opener");
      }

      get stackMediaItems() {
        return Array.from(
          this.viewer?.querySelectorAll(".product-media-item") || [],
        );
      }

      onSlideChanged(event) {
        if (!this.thumbnails || !event || !event.detail) return;

        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return;

        if (
          !thumbnail
            .querySelector(".thumbnail-media")
            .hasAttribute("aria-current")
        ) {
          this.setActiveThumbnail(thumbnail);
        }

        if (this.thumbnails.initSliderStatus) {
          this.thumbnails.moveElementToOptimalPosition(thumbnail);
        }
      }

      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        this.setActiveThumbnail(thumbnail);
        this.changeMedia(thumbnail.getAttribute("data-target"));
      }

      getVisibleStackItems() {
        return this.stackMediaItems.filter((item) => {
          if (
            item.classList.contains("product-media-item--variant") &&
            !item.classList.contains("is-current-variant-media")
          ) {
            return false;
          }

          if (
            item.hasAttribute("data-media-group") &&
            !item.classList.contains("is-current-variant-media-group")
          ) {
            return false;
          }

          return true;
        });
      }

      syncStackSeeMore() {
        const stackSeeMore = this.stackSeeMore;
        const stackSeeMoreOpener = this.stackSeeMoreOpener;

        if (!stackSeeMore || !stackSeeMoreOpener) return;

        this.stackMediaItems.forEach((item) => {
          item.classList.remove("product-media-item--stack-hidden");
        });

        if (!this.ifMediaStack || webvista.isMobileScreen()) {
          stackSeeMore.hidden = true;
          stackSeeMoreOpener.removeAttribute("data-media-id");
          return;
        }

        const visibleItems = this.getVisibleStackItems();
        const firstHiddenItem = visibleItems[this.stackSeeMoreLimit];

        if (!firstHiddenItem) {
          stackSeeMore.hidden = true;
          stackSeeMoreOpener.removeAttribute("data-media-id");
          return;
        }

        visibleItems.slice(this.stackSeeMoreLimit).forEach((item) => {
          item.classList.add("product-media-item--stack-hidden");
        });

        stackSeeMoreOpener.setAttribute(
          "data-media-id",
          firstHiddenItem.dataset.mediaId,
        );
        stackSeeMore.hidden = false;
      }

      updateGallery(currentVariantMediaId) {
        if (
          currentVariantMediaId === this.lastVariantMediaId ||
          currentVariantMediaId == null
        ) {
          return;
        }

        this.lastVariantMediaId = currentVariantMediaId;

        this.viewer
          .querySelectorAll(".is-current-variant-media")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media"),
          );
        this.viewer
          .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
          .forEach((element) => element.classList.add("is-current-variant-media"));

        if (this.thumbnails) {
          this.thumbnails
            .querySelector(".is-current-variant-thumbnail")
            ?.classList.remove("is-current-variant-thumbnail");
          this.thumbnails
            .querySelector(`[data-target="${currentVariantMediaId}"]`)
            ?.classList.add("is-current-variant-thumbnail");
        }

        if (this.hasAttribute("data-hide-variants")) {
          const groupName = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          )?.dataset.mediaGroup;

          this.viewer
            .querySelectorAll(".is-current-variant-media-group")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media-group"),
            );

          if (groupName) {
            this.viewer
              .querySelectorAll(`[data-media-group='${groupName}']`)
              .forEach((element) => {
                element.classList.add("is-current-variant-media-group");
              });
          }

          if (this.thumbnails) {
            this.thumbnails
              .querySelectorAll(".is-current-variant-thumbnail-group")
              .forEach((element) =>
                element.classList.remove("is-current-variant-thumbnail-group"),
              );

            if (groupName) {
              this.thumbnails
                .querySelectorAll(`[data-thumbnail-group='${groupName}']`)
                .forEach((element) => {
                  element.classList.add("is-current-variant-thumbnail-group");
                });
            }
          }

          if (!this.ifMediaStack || webvista.isMobileScreen()) {
            this.viewer.reInstall();
            if (this.thumbnails) this.thumbnails.reInstall();
          }
        }

        this.changeMedia(currentVariantMediaId);

        if (webvista.isMobileScreen()) {
          webvista.scrollElementToHeaderBottom(this, 16);
        }
      }

      changeMedia(mediaId) {
        const activeMedia = this.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );
        if (!activeMedia) return;

        if (this.ifMediaStack) {
          activeMedia.parentElement.prepend(activeMedia);
          this.syncStackSeeMore();
        } else if (this.viewer.initSliderStatus) {
          this.viewer.slideByElement(activeMedia);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);
      }
    },
  );
}
